import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminTenantAPI from '../../api/admin/tenants.js';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import './AdminTenantDetails.css';

const AdminTenantDetails = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get stats which includes basic tenant info (id, name, schema_name, record_counts)
        const statsRes = await AdminTenantAPI.getTenantStats(tenantId);
        const statsData = statsRes.tenant;

        // Try to get full tenant list to find additional metadata
        try {
          const tenantsRes = await AdminTenantAPI.getTenants();
          const fullTenantData = tenantsRes.tenants?.find(t => t.id === tenantId);

          if (fullTenantData) {
            // Merge stats data with full tenant metadata
            setTenant({
              ...fullTenantData,
              schema_name: statsData.schema_name
            });
          } else {
            // Fallback: use stats data only with default values
            setTenant({
              id: statsData.id,
              name: statsData.name,
              status: 'active',
              auth_method: 'unknown',
              user_count: 0,
              created_at: new Date().toISOString()
            });
          }
        } catch (listError) {
          console.warn('Could not fetch tenant list, using stats data only:', listError);
          // Fallback: use stats data only
          setTenant({
            id: statsData.id,
            name: statsData.name,
            status: 'active',
            auth_method: 'unknown',
            user_count: 0,
            created_at: new Date().toISOString()
          });
        }

        setStats(statsData);
      } catch (e) {
        setError('Failed to load tenant details');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId]);

  const formatTableName = (tableName, count) => {
    // Convert snake_case to Title Case
    const words = tableName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1));

    // Handle pluralization: singular only when count === 1, otherwise plural
    if (count === 1) {
      return words.join(' '); // Keep singular
    }

    // For count === 0 or count > 1, pluralize the last word
    const lastWord = words[words.length - 1];
    let pluralizedLastWord;

    // Check if the word is already plural (ends with 's' but not 'ss')
    if (lastWord.endsWith('s') && !lastWord.endsWith('ss') && !lastWord.endsWith('us')) {
      // Already plural (e.g., "Templates", "Submissions")
      pluralizedLastWord = lastWord;
    } else if (lastWord.endsWith('y') && !lastWord.endsWith('ay') && !lastWord.endsWith('ey') && !lastWord.endsWith('oy') && !lastWord.endsWith('uy')) {
      // e.g., "Activity" -> "Activities", "Category" -> "Categories"
      pluralizedLastWord = lastWord.slice(0, -1) + 'ies';
    } else if (lastWord.endsWith('ss') || lastWord.endsWith('ch') || lastWord.endsWith('sh') || lastWord.endsWith('x') || lastWord.endsWith('z')) {
      // e.g., "Address" -> "Addresses", "Class" -> "Classes"
      pluralizedLastWord = lastWord + 'es';
    } else {
      // Standard pluralization: add 's'
      pluralizedLastWord = lastWord + 's';
    }

    // Replace the last word with its pluralized form
    words[words.length - 1] = pluralizedLastWord;
    return words.join(' ');
  };

  const getCardColor = (count) => (count === 0 ? 'gray' : count < 10 ? 'blue' : count < 100 ? 'green' : count < 1000 ? 'orange' : 'purple');

  if (loading) {
    return (
      <AdminLayout>
        <div className="tenant-details-loading">
          <div className="loading-spinner"></div>
          <p>Loading tenant details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !tenant) {
    return (
      <AdminLayout>
        <div className="tenant-details-error">
          <h2>Error Loading Tenant</h2>
          <p>{error || 'Tenant not found'}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
          <button className="back-button" onClick={() => navigate('/admin/tenants')}>← Back to Tenants</button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-tenant-details">
        <button className="back-button" onClick={() => navigate('/admin/tenants')}>← Back to Tenants</button>

        {/* Tenant Header Section */}
        <div className="tenant-info-section">
          <div className="tenant-title">
            <h1>{tenant.name}</h1>
            <span className={`status-badge ${tenant.status}`}>{tenant.status}</span>
          </div>
          <div className="tenant-metadata">
            <div className="metadata-grid">
              <div className="metadata-item">
                <span className="metadata-label">Tenant ID</span>
                <span className="metadata-value monospace">{tenant.id}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Schema Name</span>
                <span className="metadata-value monospace">{stats?.schema_name || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Auth Method</span>
                <span className={`metadata-value auth-method ${tenant.auth_method}`}>{tenant.auth_method}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Active Users</span>
                <span className="metadata-value">{tenant.user_count}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Created</span>
                <span className="metadata-value">{new Date(tenant.created_at).toLocaleDateString()}</span>
              </div>
              {tenant.last_activity && (
                <div className="metadata-item">
                  <span className="metadata-label">Last Activity</span>
                  <span className="metadata-value">{new Date(tenant.last_activity).toLocaleDateString()}</span>
                </div>
              )}
              {tenant.stytch_organization_id && (
                <div className="metadata-item">
                  <span className="metadata-label">Stytch Org ID</span>
                  <span className="metadata-value monospace">{tenant.stytch_organization_id}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Database Statistics Section */}
        {stats && stats.record_counts && (
          <div className="stats-section">
            <h2>Database Statistics</h2>
            <div className="stats-grid">
              {Object.entries(stats.record_counts)
                .sort(([, a], [, b]) => b - a)
                .map(([tableName, count]) => (
                  <div key={tableName} className={`stat-card ${getCardColor(count)}`}>
                    <div className="stat-count">{count.toLocaleString()}</div>
                    <div className="stat-label">{formatTableName(tableName, count)}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTenantDetails;
